import * as React from "react"
import { AlertTriangle, Clock, Plug } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { useBookingT } from "@/hooks/use-translations"
import { StatusBadge } from "@/features/integrations/components/StatusBadge"
import { deriveStatus } from "@/features/integrations/components/statusModel"
import type { Connection, IntegrationCatalogItem } from "@/features/integrations/api/types"

type Props = {
  item: IntegrationCatalogItem
  connection: Connection | null
}

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return iso
  }
}

export function StatusCard({ item, connection }: Props) {
  const t = useBookingT()
  const status = deriveStatus(item, connection)
  const locale = (typeof document !== "undefined" ? document.documentElement.lang : "en") || "en"

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-neutral-900">
            {t("integrations_detail_status", "Status")}
          </h2>
          <StatusBadge status={status} />
        </div>

        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field
            icon={<Plug className="size-4 text-neutral-500" />}
            label={t("integrations_detail_connected_since", "Connected since")}
            value={formatDate(connection?.connected_at ?? null, locale)}
          />
          <Field
            icon={<Clock className="size-4 text-neutral-500" />}
            label={t("integrations_detail_last_activity", "Last activity")}
            value={formatDate(connection?.last_activity_at ?? null, locale)}
          />
          {connection?.last_error && (
            <Field
              icon={<AlertTriangle className="size-4 text-error-500" />}
              label={t("integrations_detail_last_error", "Last error")}
              value={connection.last_error.message}
              valueClassName="text-error-700"
            />
          )}
        </dl>
      </CardContent>
    </Card>
  )
}

function Field({
  icon,
  label,
  value,
  valueClassName,
}: {
  icon: React.ReactNode
  label: string
  value: string
  valueClassName?: string
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="mt-0.5">{icon}</div>
      <div className="min-w-0">
        <dt className="text-xs uppercase tracking-wide text-neutral-700">{label}</dt>
        <dd className={`mt-0.5 text-sm text-neutral-900 ${valueClassName ?? ""}`}>{value}</dd>
      </div>
    </div>
  )
}
